/**
 * Voice Platform may emit either the call fields directly or an envelope
 * `{ event: string, payload: object }`.
 *
 * @param {unknown} raw
 * @returns {unknown}
 */
export function unwrapVoiceSocketEventPayload(raw) {
  if (!raw || typeof raw !== 'object') return raw;

  const record = /** @type {Record<string, unknown>} */ (raw);
  const inner = record.payload;

  if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
    const hasEnvelopeShape = typeof record.event === 'string' || 'payload' in record;
    if (hasEnvelopeShape) {
      return inner;
    }
  }

  return raw;
}

/**
 * @param {unknown} record
 * @returns {string|null}
 */
export function readCallControlId(record) {
  if (!record || typeof record !== 'object') return null;
  const data = /** @type {Record<string, unknown>} */ (record);
  const value = data.callControlId ?? data.call_control_id;
  return typeof value === 'string' && value ? value : null;
}

/**
 * @param {unknown} payload
 * @returns {{ callControlId: string, tabSessionId: string, autoAnswer?: boolean, direction?: string }|null}
 */
export function parseAgentSipInvitePayload(payload) {
  const data = unwrapVoiceSocketEventPayload(payload);
  if (!data || typeof data !== 'object') return null;

  const record = /** @type {Record<string, unknown>} */ (data);
  const callControlId = readCallControlId(record);
  const tabSessionId = record.tabSessionId ?? record.tab_session_id;
  const autoAnswer = record.autoAnswer ?? record.auto_answer;
  const { direction } = record;
  if (!callControlId) return null;
  if (typeof tabSessionId !== 'string' || !tabSessionId) return null;

  return {
    callControlId,
    tabSessionId,
    autoAnswer: autoAnswer === true,
    direction: typeof direction === 'string' ? direction : undefined
  };
}

/**
 * @param {unknown} payload
 * @returns {import('../../domain/voiceTypes').IncomingCallPayload|null}
 */
export function parseIncomingCallPayload(payload) {
  const data = unwrapVoiceSocketEventPayload(payload);
  if (!data || typeof data !== 'object') return null;

  const record = /** @type {Record<string, unknown>} */ (data);
  const callControlId = readCallControlId(record);
  const {
    from,
    to,
    timestamp,
    channel,
    direction
  } = record;

  if (!callControlId) return null;
  if (typeof from !== 'string' || !from) return null;
  if (typeof to !== 'string' || !to) return null;

  const callSessionId = record.callSessionId ?? record.call_session_id;
  const callLegId = record.callLegId ?? record.call_leg_id;

  let normalizedTimestamp = new Date().toISOString();
  if (typeof timestamp === 'string') {
    normalizedTimestamp = timestamp;
  } else if (typeof timestamp === 'number') {
    normalizedTimestamp = new Date(timestamp).toISOString();
  }

  return {
    callControlId,
    callSessionId: typeof callSessionId === 'string' ? callSessionId : '',
    callLegId: typeof callLegId === 'string' ? callLegId : '',
    from,
    to,
    direction: typeof direction === 'string' ? direction : '',
    timestamp: normalizedTimestamp,
    channel: typeof channel === 'string' ? channel : 'unknown'
  };
}

/**
 * @param {unknown} payload
 * @returns {import('../../domain/voiceTypes').CallAnsweredPayload|null}
 */
export function parseCallAnsweredPayload(payload) {
  const data = unwrapVoiceSocketEventPayload(payload);
  if (!data || typeof data !== 'object') return null;

  const record = /** @type {Record<string, unknown>} */ (data);
  const callControlId = readCallControlId(record);
  const callSessionId = record.callSessionId ?? record.call_session_id;
  const callLegId = record.callLegId ?? record.call_leg_id;
  const answeredAt = record.answeredAt ?? record.answered_at;
  const tabSessionId = record.tabSessionId ?? record.tab_session_id;
  const {
    from, to, direction, channel
  } = record;

  if (!callControlId) {
    return null;
  }

  return {
    callControlId,
    callSessionId: typeof callSessionId === 'string' ? callSessionId : '',
    callLegId: typeof callLegId === 'string' ? callLegId : '',
    from: typeof from === 'string' ? from : '',
    to: typeof to === 'string' ? to : '',
    direction: typeof direction === 'string' ? direction : '',
    channel: typeof channel === 'string' ? channel : 'unknown',
    answeredAt: typeof answeredAt === 'string' ? answeredAt : '',
    tabSessionId: typeof tabSessionId === 'string' && tabSessionId.trim()
      ? tabSessionId.trim()
      : undefined
  };
}

/**
 * @param {unknown} payload
 * @returns {import('../../domain/voiceTypes').CallControlPayload|null}
 */
export function parseCallControlPayload(payload) {
  const data = unwrapVoiceSocketEventPayload(payload);
  if (!data || typeof data !== 'object') return null;
  const callControlId = readCallControlId(data);
  if (!callControlId) return null;
  return { callControlId };
}

/**
 * @param {unknown} payload
 * @returns {{ callControlId: string, hangupCause?: string }|null}
 */
export function parseCallHangupPayload(payload) {
  const data = unwrapVoiceSocketEventPayload(payload);
  if (!data || typeof data !== 'object') return null;
  const record = /** @type {Record<string, unknown>} */ (data);
  const callControlId = readCallControlId(record);
  if (!callControlId) return null;
  let hangupCause;
  if (typeof record.hangupCause === 'string') {
    hangupCause = record.hangupCause;
  } else if (typeof record.hangup_cause === 'string') {
    hangupCause = record.hangup_cause;
  }
  return { callControlId, hangupCause };
}

/**
 * @param {unknown} payload
 * @returns {import('../../domain/voiceTypes').CallFailedPayload|null}
 */
export function parseCallFailedPayload(payload) {
  const data = unwrapVoiceSocketEventPayload(payload);
  if (!data || typeof data !== 'object') return null;
  const record = /** @type {Record<string, unknown>} */ (data);
  const callControlId = readCallControlId(record);
  const reason = record.reason ?? record.error;
  if (!callControlId || typeof reason !== 'string') return null;
  return { callControlId, reason };
}
